import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { getTemplate } from 'team/apis/teamTemplateApi';
import { useAuth } from 'contexts/AuthContext';
import { useError } from 'contexts/ErrorContext';

// Create the TeamTemplateContext
const TeamTemplateContext = createContext();

export const TeamTemplateProvider = ({ children }) => {
    const { authState } = useAuth();
    const { handleApiCall } = useError();

    const [templateState, setTemplateState] = useState({});
    const [templateCache, setTemplateCache] = useState(new Map());
    const [pendingRequests, setPendingRequests] = useState(new Map());
    const loadedTemplatesRef = useRef(new Set());

    // Load from localStorage on mount (similar to CardContext)
    useEffect(() => {
        if (authState.isAuthenticated) {
            const savedState = localStorage.getItem('templateState');
            if (savedState) {
                try {
                    const parsedState = JSON.parse(savedState);
                    setTemplateState(parsedState);
                } catch (error) {
                    console.error('Error parsing templateState from localStorage:', error);
                    setTemplateState({});
                }
            }
        } else {
            // Clear template state if not authenticated
            setTemplateState({});
        }
    }, [authState.isAuthenticated]);

    // Initial values structure for each template
    const initialTemplateValues = {
        templateId: null,
        templateName: '',
        about: {
            fullName: '',
            jobTitle: '',
            company: '',
            location: '',
            bio: '',
            cardLayout: 'left-aligned',
            cardTheme: '',
            linkColor: '#000000',
            font: '',
            matchLinkIconsToTheme: false,
            profilePictureUrl: '',
            coverPhotoUrl: '',
            companyLogoUrl: '',
            customLinkTypeLabels: {
                'Contact': 'Contact',
                'Social': 'Social',
                'Products & Services': 'Products & Services'
            }
        },
        links: {},
        qrCode: {},
        leadCapture: {},
        followUpEmail: {},
        settings: {
            allowNonAdminsToAssign: false
        }
    };

    // Update template state with localStorage persistence
    const updateTemplateState = useCallback((templateId, data) => {
        setTemplateState(prev => {
            const newState = {
                ...prev,
                [templateId]: {
                    ...prev[templateId],
                    ...data
                }
            };

            // Save to localStorage
            localStorage.setItem('templateState', JSON.stringify(newState));
            return newState;
        });
    }, []);

    // Set specific template field
    const setTemplateField = useCallback((templateId, field, value) => {
        setTemplateState(prev => {
            const newState = {
                ...prev,
                [templateId]: {
                    ...prev[templateId],
                    [field]: value
                }
            };

            localStorage.setItem('templateState', JSON.stringify(newState));
            return newState;
        });
    }, []);

    // Set template section (about, links, qrCode, leadCapture, followUpEmail)
    const setTemplateSection = useCallback((templateId, section, data) => {
        setTemplateState(prev => {
            const newState = {
                ...prev,
                [templateId]: {
                    ...prev[templateId],
                    [section]: {
                        ...(prev[templateId]?.[section] || {}),
                        ...data
                    }
                }
            };

            localStorage.setItem('templateState', JSON.stringify(newState));
            return newState;
        });
    }, []);

    // Create secure updater function (similar to CardContext pattern)
    const createSecureTemplateUpdater = useCallback((fieldName) => {
        return (templateId, value) => {
            updateTemplateState(templateId, { [fieldName]: value });
        };
    }, [updateTemplateState]);

    // Individual setter functions for image URLs (similar to CardContext)
    const setTemplateProfilePictureUrl = useCallback(createSecureTemplateUpdater('templateProfilePictureUrl'), [createSecureTemplateUpdater]);
    const setTemplateCoverPhotoUrl = useCallback(createSecureTemplateUpdater('templateCoverPhotoUrl'), [createSecureTemplateUpdater]);
    const setTemplateCompanyLogoUrl = useCallback(createSecureTemplateUpdater('templateCompanyLogoUrl'), [createSecureTemplateUpdater]);

    // Helper to update about section image URLs
    const updateAboutImageUrl = useCallback((templateId, imageType, url) => {
        console.log('[TeamTemplateContext] 🖼️ updateAboutImageUrl called:', {
            templateId,
            imageType,
            url
        });
        setTemplateState(prev => {
            const currentAbout = prev[templateId]?.about || {};
            const newState = {
                ...prev,
                [templateId]: {
                    ...prev[templateId],
                    about: {
                        ...currentAbout,
                        [imageType]: url
                    }
                }
            };

            console.log('[TeamTemplateContext] 🖼️ Updated template state:', {
                templateId,
                imageType,
                url,
                newAbout: newState[templateId]?.about
            });

            localStorage.setItem('templateState', JSON.stringify(newState));
            return newState;
        });
    }, []);

    // Wrapper functions that update the about section (matches how templates store images)
    const setTemplateAboutProfilePictureUrl = useCallback((templateId, url) => {
        updateAboutImageUrl(templateId, 'profilePictureUrl', url);
    }, [updateAboutImageUrl]);

    const setTemplateAboutCoverPhotoUrl = useCallback((templateId, url) => {
        updateAboutImageUrl(templateId, 'coverPhotoUrl', url);
    }, [updateAboutImageUrl]);

    const setTemplateAboutCompanyLogoUrl = useCallback((templateId, url) => {
        updateAboutImageUrl(templateId, 'companyLogoUrl', url);
    }, [updateAboutImageUrl]);

    // Load template from server (similar to loadCardFromServer)
    const loadTemplateFromServer = useCallback(async (teamId, templateId) => {
        try {
            if (!teamId || !templateId) {
                console.log('🔍 TemplateContext: Missing teamId or templateId');
                return null;
            }

            // Check if we already loaded this template recently
            if (loadedTemplatesRef.current.has(templateId)) {
                return templateState[templateId] || null;
            }

            // Check cache first (5 minute cache)
            const cached = templateCache.get(templateId);
            if (cached && Date.now() - cached.timestamp < 5 * 60 * 1000) {
                return cached.data;
            }

            // Request deduplication
            if (pendingRequests.has(templateId)) {
                return await pendingRequests.get(templateId);
            }

            // Create a promise for this request
            const requestPromise = (async () => {
                console.log('🔍 TemplateContext: Loading template from server:', templateId);

                const response = await handleApiCall(
                    () => getTemplate(teamId, templateId),
                    {
                        source: 'template-load',
                        retryable: true,
                        autoDismiss: true
                    }
                );

                if (response && response.template) {
                    const serverTemplate = response.template;

                    // Map server response to template state format
                    // CRITICAL: Preserve blob URLs from context (for real-time preview)
                    // Server returns null for blob URLs since they're not saved to database
                    const currentContextAbout = templateState[templateId]?.about || {};
                    const serverAbout = serverTemplate.about || {};
                    
                    // Preserve blob URLs from context - don't overwrite with server's null
                    const preservedAbout = {
                        ...initialTemplateValues.about,
                        ...serverAbout,
                        // Preserve blob URLs if they exist in context (for real-time preview)
                        profilePictureUrl: (currentContextAbout.profilePictureUrl && currentContextAbout.profilePictureUrl.startsWith('blob:'))
                            ? currentContextAbout.profilePictureUrl
                            : (serverAbout.profilePictureUrl || null),
                        coverPhotoUrl: (currentContextAbout.coverPhotoUrl && currentContextAbout.coverPhotoUrl.startsWith('blob:'))
                            ? currentContextAbout.coverPhotoUrl
                            : (serverAbout.coverPhotoUrl || null),
                        companyLogoUrl: (currentContextAbout.companyLogoUrl && currentContextAbout.companyLogoUrl.startsWith('blob:'))
                            ? currentContextAbout.companyLogoUrl
                            : (serverAbout.companyLogoUrl || null),
                    };
                    
                    const templateData = {
                        ...initialTemplateValues,
                        ...templateState[templateId], // Keep existing local changes
                        // Override with server values
                        templateId: serverTemplate._id,
                        templateName: serverTemplate.templateName || '',
                        about: preservedAbout,
                        links: serverTemplate.links || {},
                        qrCode: serverTemplate.qrCode || {},
                        leadCapture: serverTemplate.leadCapture || {},
                        followUpEmail: serverTemplate.followUpEmail || {},
                        settings: serverTemplate.settings || {
                            allowNonAdminsToAssign: false
                        }
                    };

                    // Log image URLs when loading from server
                    console.log('[TeamTemplateContext] Loaded template from server with image URLs:', {
                        templateId: serverTemplate._id,
                        profilePictureUrl: templateData.about.profilePictureUrl,
                        coverPhotoUrl: templateData.about.coverPhotoUrl,
                        companyLogoUrl: templateData.about.companyLogoUrl
                    });

                    setTemplateState(prev => {
                        const newState = {
                            ...prev,
                            [templateId]: templateData
                        };

                        localStorage.setItem('templateState', JSON.stringify(newState));
                        return newState;
                    });

                    // Cache the result
                    setTemplateCache(prev => new Map(prev).set(templateId, {
                        data: serverTemplate,
                        timestamp: Date.now()
                    }));

                    // Mark as recently loaded
                    loadedTemplatesRef.current.add(templateId);

                    // Clear the "recently loaded" flag after 30 seconds
                    setTimeout(() => {
                        loadedTemplatesRef.current.delete(templateId);
                    }, 30000);
                }

                return response.template;
            })();

            // Store the promise in pending requests
            setPendingRequests(prev => new Map(prev).set(templateId, requestPromise));

            try {
                const result = await requestPromise;
                return result;
            } finally {
                // Clean up the pending request
                setPendingRequests(prev => {
                    const newMap = new Map(prev);
                    newMap.delete(templateId);
                    return newMap;
                });
            }
        } catch (error) {
            console.error('TemplateContext: Error loading template:', error);
            throw error;
        }
    }, [templateState, templateCache, handleApiCall, initialTemplateValues, pendingRequests]);

    // Get template state
    const getTemplateState = useCallback((templateId) => {
        return templateState[templateId] || {};
    }, [templateState]);

    // Clear template state
    const clearTemplateState = useCallback((templateId) => {
        setTemplateState(prev => {
            const newState = { ...prev };
            delete newState[templateId];
            localStorage.setItem('templateState', JSON.stringify(newState));
            return newState;
        });
    }, []);

    return (
        <TeamTemplateContext.Provider
            value={{
                templateState,
                updateTemplateState,
                setTemplateField,
                setTemplateSection,
                getTemplateState,
                clearTemplateState,
                // Image URL setters (for about section)
                setTemplateAboutProfilePictureUrl,
                setTemplateAboutCoverPhotoUrl,
                setTemplateAboutCompanyLogoUrl,
                // Direct image URL setters (for top-level)
                setTemplateProfilePictureUrl,
                setTemplateCoverPhotoUrl,
                setTemplateCompanyLogoUrl,
                // Load from server
                loadTemplateFromServer
            }}
        >
            {children}
        </TeamTemplateContext.Provider>
    );
};

// Custom hook to use the TeamTemplateContext
export const useTeamTemplateContext = () => {
    const context = useContext(TeamTemplateContext);
    if (!context) {
        throw new Error('useTeamTemplateContext must be used within a TeamTemplateProvider');
    }
    return context;
};

